import { type ChangeEvent, type FormEvent, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  useDeleteFireExperiment,
  useFireExperiment,
  useFireTestFacets,
  useUpsertFireExperiment,
} from "@/hooks/useFireTests";
import type { FireExperiment, FireExperimentUpsert } from "@/types";
import { apiErrorDetail } from "@/utils/apiError";
import { hasMinRole } from "@/utils/permissions";

/**
 * 시료 상세의 '화재시험 조건' (PLAN 2-11, 2026-10-10).
 *
 * 이 조건이 있는 시료가 참조물질DB '모의현장 DB' 에 든다 — 실제 사건 시료와
 * 실험 자료를 가르는 유일한 표시다. 입력·수정은 분석관 이상, 해제는 수사관
 * 이상(서버 권한과 같다). 해제해도 시료·스펙트럼은 그대로다.
 */

type FormState = {
  substrate: string;
  ignitable_liquid: string;
  evaporation_level: string;
  experiment_date: string;
  il_amount: string;
  combustion_method: string;
  instrument_name: string;
  notes: string;
  il_application_position: string;
  combustion_temperature: string;
  collection_duration: string;
  collection_flow_rate: string;
  gc_column: string;
  analysis_conditions: string;
  is_mixture: boolean;
  material_ratio: string;
};

const TEXT_FIELDS: { key: keyof FormState; label: string }[] = [
  { key: "il_amount", label: "인화성 액체 양" },
  { key: "combustion_method", label: "연소 방법" },
  { key: "instrument_name", label: "분석 기기" },
];

const MORE_FIELDS: { key: keyof FormState; label: string }[] = [
  { key: "il_application_position", label: "도포 위치" },
  { key: "collection_duration", label: "포집 시간" },
  { key: "collection_flow_rate", label: "포집 유량" },
  { key: "gc_column", label: "GC 컬럼" },
];

function toForm(value: FireExperiment | null | undefined): FormState {
  const text = (v: string | number | null | undefined) =>
    v == null ? "" : String(v);
  return {
    substrate: text(value?.substrate),
    ignitable_liquid: text(value?.ignitable_liquid),
    evaporation_level: text(value?.evaporation_level),
    experiment_date: value?.experiment_date
      ? value.experiment_date.slice(0, 10)
      : "",
    il_amount: text(value?.il_amount),
    combustion_method: text(value?.combustion_method),
    instrument_name: text(value?.instrument_name),
    notes: text(value?.notes),
    il_application_position: text(value?.il_application_position),
    combustion_temperature: text(value?.combustion_temperature),
    collection_duration: text(value?.collection_duration),
    collection_flow_rate: text(value?.collection_flow_rate),
    gc_column: text(value?.gc_column),
    analysis_conditions: text(value?.analysis_conditions),
    is_mixture: !!value?.is_mixture,
    material_ratio: text(value?.material_ratio),
  };
}

function optionalText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : null;
}

/** 폼 값 → 저장 요청. 필수 칸이 비면 null(보내지 않는다). */
function toPayload(form: FormState): FireExperimentUpsert | null {
  const substrate = form.substrate.trim();
  const ignitableLiquid = form.ignitable_liquid.trim();
  const evaporation = Number(form.evaporation_level);
  if (!substrate || !ignitableLiquid || !evaporation) return null;
  return {
    substrate,
    ignitable_liquid: ignitableLiquid,
    evaporation_level: evaporation,
    experiment_date: form.experiment_date
      ? `${form.experiment_date}T00:00:00`
      : null,
    il_amount: optionalText(form.il_amount),
    combustion_method: optionalText(form.combustion_method),
    instrument_name: optionalText(form.instrument_name),
    notes: optionalText(form.notes),
    il_application_position: optionalText(form.il_application_position),
    combustion_temperature: optionalNumber(form.combustion_temperature),
    collection_duration: optionalText(form.collection_duration),
    collection_flow_rate: optionalText(form.collection_flow_rate),
    gc_column: optionalText(form.gc_column),
    analysis_conditions: optionalText(form.analysis_conditions),
    is_mixture: form.is_mixture,
    material_ratio: optionalNumber(form.material_ratio),
  };
}

const inputClass =
  "w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none";

function ConditionForm({
  sampleId,
  initial,
  onDone,
}: {
  sampleId: number;
  initial: FireExperiment | null | undefined;
  onDone: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => toForm(initial));
  const [missing, setMissing] = useState(false);
  const upsert = useUpsertFireExperiment(sampleId);
  // 같은 값은 같은 표기로 — 모의현장 DB 집계가 표기 차이로 갈라지지 않게.
  // 목록 API 는 MS 총계 때문에 무거워 가벼운 집계 API 를 쓴다(독립 검토).
  const { data: facets } = useFireTestFacets();

  const set =
    (key: keyof FormState) =>
    (
      e: ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const payload = toPayload(form);
    if (!payload) {
      setMissing(true);
      return;
    }
    setMissing(false);
    upsert.mutate(payload, { onSuccess: onDone });
  };

  const field = (key: keyof FormState, label: string) => (
    <label key={key} className="block text-xs text-gray-600">
      <span className="mb-1 block">{label}</span>
      <input
        aria-label={label}
        value={form[key] as string}
        onChange={set(key)}
        className={inputClass}
      />
    </label>
  );

  const numberField = (
    key: "combustion_temperature" | "material_ratio",
    label: string,
    range?: { min: number; max: number },
  ) => (
    <label key={key} className="block text-xs text-gray-600">
      <span className="mb-1 block">{label}</span>
      <input
        aria-label={label}
        type="number"
        step={range ? 1 : "any"}
        min={range?.min}
        max={range?.max}
        value={form[key]}
        onChange={set(key)}
        className={inputClass}
      />
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <label className="block text-xs text-gray-600">
          <span className="mb-1 block">재질 *</span>
          <input
            aria-label="재질 *"
            list="fire-substrate-options"
            value={form.substrate}
            onChange={set("substrate")}
            className={inputClass}
          />
          <datalist id="fire-substrate-options">
            {facets?.substrate.map((f) => (
              <option key={f.value} value={f.value} />
            ))}
          </datalist>
        </label>
        <label className="block text-xs text-gray-600">
          <span className="mb-1 block">인화성 액체 *</span>
          <input
            aria-label="인화성 액체 *"
            list="fire-il-options"
            value={form.ignitable_liquid}
            onChange={set("ignitable_liquid")}
            className={inputClass}
          />
          <datalist id="fire-il-options">
            {facets?.ignitable_liquid.map((f) => (
              <option key={f.value} value={f.value} />
            ))}
          </datalist>
        </label>
        <label className="block text-xs text-gray-600">
          <span className="mb-1 block">증발 단계 *</span>
          <select
            aria-label="증발 단계 *"
            value={form.evaporation_level}
            onChange={set("evaporation_level")}
            className={inputClass}
          >
            <option value="">선택</option>
            {[1, 2, 3, 4, 5].map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-600">
          <span className="mb-1 block">실험일</span>
          <input
            aria-label="실험일"
            type="date"
            value={form.experiment_date}
            onChange={set("experiment_date")}
            className={inputClass}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {TEXT_FIELDS.map(({ key, label }) => field(key, label))}
      </div>

      <details className="rounded border border-gray-200 p-3">
        <summary className="cursor-pointer text-xs font-medium text-gray-600">
          추가 조건 (선택)
        </summary>
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
          {MORE_FIELDS.map(({ key, label }) => field(key, label))}
          {numberField("combustion_temperature", "연소 온도(℃)")}
          {numberField("material_ratio", "혼합 비율", { min: 0, max: 100 })}
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={form.is_mixture}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, is_mixture: e.target.checked }))
              }
            />
            혼합물
          </label>
        </div>
        <label className="mt-3 block text-xs text-gray-600">
          <span className="mb-1 block">분석 조건</span>
          <textarea
            aria-label="분석 조건"
            value={form.analysis_conditions}
            onChange={set("analysis_conditions")}
            rows={2}
            className={inputClass}
          />
        </label>
      </details>

      <label className="block text-xs text-gray-600">
        <span className="mb-1 block">메모</span>
        <textarea
          aria-label="메모"
          value={form.notes}
          onChange={set("notes")}
          rows={2}
          className={inputClass}
        />
      </label>

      {missing && (
        <p className="text-sm text-red-700">
          재질·인화성 액체·증발 단계를 입력하세요.
        </p>
      )}
      {upsert.isError && (
        <p className="text-sm text-red-700">
          {apiErrorDetail(upsert.error, "저장하지 못했습니다.")}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={upsert.isPending}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          저장
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          취소
        </button>
      </div>
    </form>
  );
}

function ConditionView({ value }: { value: FireExperiment }) {
  const rows: [string, string | number | null | undefined][] = [
    ["재질", value.substrate],
    ["인화성 액체", value.ignitable_liquid],
    ["증발 단계", value.evaporation_level],
    [
      "실험일",
      value.experiment_date
        ? value.experiment_date.slice(0, 10).replaceAll("-", ".")
        : null,
    ],
    ["인화성 액체 양", value.il_amount],
    ["연소 방법", value.combustion_method],
    ["분석 기기", value.instrument_name],
    ["도포 위치", value.il_application_position],
    ["연소 온도(℃)", value.combustion_temperature],
    ["포집 시간", value.collection_duration],
    ["포집 유량", value.collection_flow_rate],
    ["GC 컬럼", value.gc_column],
    ["분석 조건", value.analysis_conditions],
    ["메모", value.notes],
  ];
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-4">
      {rows
        .filter(([, v]) => v != null && v !== "")
        .map(([label, v]) => (
          <div key={label}>
            <dt className="text-xs font-medium text-gray-500">{label}</dt>
            <dd className="text-sm text-gray-900 whitespace-pre-wrap">
              {String(v)}
            </dd>
          </div>
        ))}
    </dl>
  );
}

export default function FireExperimentSection({
  sampleId,
}: {
  sampleId: number;
}) {
  const { user } = useAuth();
  const canEdit = user ? hasMinRole(user.role, "ANALYST") : false;
  const canRemove = user ? hasMinRole(user.role, "INVESTIGATOR") : false;
  const { data: condition, isLoading, isError } = useFireExperiment(sampleId);
  const remove = useDeleteFireExperiment(sampleId);
  const [editing, setEditing] = useState(false);

  const handleRemove = () => {
    if (
      window.confirm(
        "이 시료를 모의현장 DB 에서 뺄까요? 시료와 스펙트럼은 그대로 남습니다.",
      )
    ) {
      remove.mutate();
    }
  };

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">화재시험 조건</h2>
          <p className="text-xs text-gray-500">
            조건이 입력된 시료는 참조물질DB '모의현장 DB' 에 모입니다.
          </p>
        </div>
        {!editing && condition && (
          <div className="flex shrink-0 gap-2">
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                수정
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={remove.isPending}
                className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                모의현장 DB 에서 해제
              </button>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="h-8 animate-pulse rounded bg-gray-200" />
      ) : isError ? (
        // 불러오지 못했을 때 '없음'으로 보이면 입력이 기존 조건을 덮어쓴다
        <p className="text-sm text-red-700">
          화재시험 조건을 불러오지 못했습니다. 새로고침 후 다시 시도하세요.
        </p>
      ) : editing ? (
        <ConditionForm
          sampleId={sampleId}
          initial={condition}
          onDone={() => setEditing(false)}
        />
      ) : condition ? (
        <ConditionView value={condition} />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            화재시험 조건이 없어 모의현장 DB 에 들어가지 않은 시료입니다.
          </p>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              화재시험 조건 입력
            </button>
          )}
        </div>
      )}
      {remove.isError && (
        <p className="mt-3 text-sm text-red-700">
          {apiErrorDetail(remove.error, "해제하지 못했습니다.")}
        </p>
      )}
    </div>
  );
}
